import { RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FONTS = ["Roboto", "Open Sans", "Lato", "Montserrat", "Source Sans 3", "Nunito", "Poppins", "Arial", "Georgia"];
const DEFAULTS = {
  font_family: "Roboto", font_size: 17, line_height: 26, text_color: "#1f2937",
  background_color: "#ffffff", accent_color: "#0369a1", border_color: "#d7dee6", radius: 4, show_credit: true,
};
const COLORS = [
  ["accent_color", "Aksentfarge (knapp og sum)"],
  ["text_color", "Tekstfarge"],
  ["background_color", "Bakgrunn"],
  ["border_color", "Linjer og rammer"],
];

const ColorField = ({ k, label, value, onChange }) => (
  <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-white p-2">
    <input type="color" value={value} onChange={(e) => onChange(k, e.target.value)}
      className="w-10 h-10 rounded cursor-pointer border-0 bg-transparent p-0" data-testid={`color-${k}`} aria-label={label} />
    <div className="min-w-0">
      <div className="text-sm font-medium text-slate-800 truncate">{label}</div>
      <div className="text-xs text-slate-500 font-num uppercase">{value}</div>
    </div>
  </div>
);

const SliderField = ({ label, value, min, max, onChange, testId }) => (
  <div className="space-y-2">
    <div className="flex justify-between text-sm"><Label>{label}</Label><span className="font-num text-slate-600" data-testid={`${testId}-value`}>{value}px</span></div>
    <Slider value={[value]} min={min} max={max} step={1} onValueChange={([v]) => onChange(v)} data-testid={testId} />
  </div>
);

export const AppearancePanel = ({ settings, onChange }) => {
  const set = (k, v) => onChange({ ...settings, [k]: v });
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label>Font</Label>
          <Select value={settings.font_family} onValueChange={(v) => set("font_family", v)}>
            <SelectTrigger className="h-10 bg-white" data-testid="appearance-font-select"><SelectValue /></SelectTrigger>
            <SelectContent>{FONTS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <SliderField label="Hjørneradius" value={settings.radius} min={0} max={24} onChange={(v) => set("radius", v)} testId="appearance-radius" />
        <SliderField label="Skriftstørrelse" value={settings.font_size} min={12} max={26} onChange={(v) => set("font_size", v)} testId="appearance-font-size" />
        <SliderField label="Linjeavstand" value={settings.line_height} min={16} max={40} onChange={(v) => set("line_height", v)} testId="appearance-line-height" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {COLORS.map(([k, label]) => <ColorField key={k} k={k} label={label} value={settings[k]} onChange={set} />)}
      </div>
      <Button variant="outline" size="sm" onClick={() => onChange({ ...DEFAULTS })} data-testid="appearance-reset">
        <RotateCcw className="w-4 h-4 mr-2" /> Tilbakestill til standard (Roboto 17px / 26px)
      </Button>
      <CreditToggle checked={settings.show_credit !== false} onChange={(v) => set("show_credit", v)} />
    </div>
  );
};

const CreditToggle = ({ checked, onChange }) => (
  <details className="group text-xs text-slate-400" data-testid="credit-details">
    <summary className="cursor-pointer select-none w-fit hover:text-slate-600" data-testid="credit-details-summary">Flere valg</summary>
    <label className="mt-3 flex items-center gap-3 text-sm text-slate-600">
      <Switch checked={checked} onCheckedChange={onChange} data-testid="credit-toggle" />
      Vis «Vi bruker Havnekalkulator.no» i kalkulatoren
    </label>
  </details>
);
